import {
  Checkbox,
  Collapse,
  FormControlLabel,
  Grid,
  Typography
} from '@mui/material';
import { formatNumber } from '../../../pricing-simulator';
import { ItemDetails, IOpportunity } from '../../types/ppg-sku-drawer.model';
import ItemHeader from './item-header';
import CommentInputControl from '../comments/comment-input-control';
import ProductCommentsContainer from '../comments/comment-list';

interface IProductDetailsBoxProps {
  /**
   * Details of the Product (PPG or SKU)
   */
  productDetails: ItemDetails;
  /**
   * Opportunities available for the displayed product
   */
  opportunities: IOpportunity[];
  /**
   * Are the details shown for PPG
   */
  isPPGBox: boolean;
  /**
   * An event handler called on change of opportunity selection by user
   * @param isOpportunityChecked Is opportunity checked or unchecked by user
   * @param selectedOpportunities New list of selected opportunities after user selection
   * @param changedOpportunity Opportunity metadata of the changed opportunity
   * @returns void
   */
  onOpportunitySelection: (
    isOpportunityChecked: boolean,
    selectedOpportunities: number[],
    changedOpportunity: IOpportunity
  ) => void;
  /**
   * Whether the box is expandable
   */
  isExpandable: boolean;
  /**
   * Expanded state of collapse if expandable is true
   */
  isOpen: boolean;

  /**
   * Is opportunity selection disabled. Default `false`
   */
  // eslint-disable-next-line react/require-default-props
  isOpportunitiesSelectionDisabled?: boolean;

  /**
   * Are comments editable?. Default `true`
   */
  // eslint-disable-next-line react/require-default-props
  isCommentEditable?: boolean;

  /**
   * Event handler called on click of expandable button click
   * @returns void
   */
  toggleExpandableState: () => void;
  onCommentAdd: (comment: string) => Promise<unknown>;
  onCommentUpdate: (
    commentId: number,
    updatedComment: string
  ) => Promise<unknown>;
  onCommentDelete: (commentId: number) => Promise<unknown>;
}

interface IOpportunityStripProps {
  selectedOpportunities: number[];
  /**
   * An event handler called on change of opportunity selection by user
   * @param isOpportunityChecked Is opportunity checked or unchecked by user
   * @param selectedOpportunities New list of selected opportunities after user selection
   * @param changedOpportunity Opportunity metadata of the changed opportunity
   * @returns void
   */
  onOpportunitySelection: (
    isOpportunityChecked: boolean,
    selectedOpportunities: number[],
    changedOpportunity: IOpportunity
  ) => void;
  /**
   * Opportunities available for the displayed product
   */
  opportunities: IOpportunity[];
  /**
   * Is opportunity disabled
   */
  // eslint-disable-next-line react/require-default-props
  isDisabled?: boolean;
}

export function OpportunityStrip({
  selectedOpportunities,
  onOpportunitySelection,
  opportunities,
  isDisabled = false
}: IOpportunityStripProps) {
  const isOpportunityDisabled = (opportunity: IOpportunity) =>
    isDisabled || !opportunity.is_active;
  const handleOpportunityChange = (opportunity: IOpportunity) => {
    const selectedOpportunityId = opportunity.pk_id;
    const checked = !selectedOpportunities.includes(selectedOpportunityId);
    const intermediateOpportunityIdsDisabledBySelectedOpportunity =
      opportunities
        .filter((opp) => opportunity.disables_opportunity.includes(opp.pk_id))
        .map((opp) => opp.intermediate_opp_id);
    const newSelectedOpportunities = checked
      ? [
          ...selectedOpportunities.filter(
            (opportunityId) =>
              opportunityId !== opportunity.intermediate_opp_id &&
              !intermediateOpportunityIdsDisabledBySelectedOpportunity.includes(
                opportunityId
              )
          ),
          selectedOpportunityId
        ]
      : selectedOpportunities.filter((id) => id !== selectedOpportunityId);

    onOpportunitySelection(checked, newSelectedOpportunities, opportunity);
  };

  return (
    <>
      <Grid item xs={12} pt={1}>
        <Typography fontSize='12px' fontWeight={700}>
          Select an Opportunity
        </Typography>
      </Grid>
      <Grid container alignItems='center' py={1} pl={1} item xs={12}>
        {opportunities.map((opportunity) => (
          <Grid item xs key={opportunity.pk_id}>
            <FormControlLabel
              onClick={() => {
                if (isOpportunityDisabled(opportunity)) {
                  return;
                }
                handleOpportunityChange(opportunity);
              }}
              disabled={isOpportunityDisabled(opportunity)}
              label={
                <Typography fontSize='12px'>{opportunity.label} </Typography>
              }
              control={
                <Checkbox
                  indeterminate={selectedOpportunities.includes(
                    opportunity.intermediate_opp_id
                  )}
                  checked={selectedOpportunities.includes(opportunity.pk_id)}
                  sx={{ '&.MuiCheckbox-root': { p: 0.5 } }}
                  size='small'
                />
              }
            />
          </Grid>
        ))}
      </Grid>
    </>
  );
}

/**
 * Renders the details for a given product (PPG or SKU)
 * Product Name, Image, Metrics, Opportunities and Comments
 */
export default function ProductDetailsBox({
  productDetails,
  opportunities,
  onOpportunitySelection,
  isOpen,
  isExpandable,
  isOpportunitiesSelectionDisabled = false,
  isCommentEditable = true,
  toggleExpandableState,
  onCommentAdd,
  onCommentUpdate,
  onCommentDelete,
  isPPGBox = true
}: IProductDetailsBoxProps) {
  return (
    <Grid
      container
      sx={{
        border: '1px solid #D9D9D9',
        backgroundColor: 'white',
        borderRadius: 0.5
      }}
    >
      <Grid container alignItems='center' item xs={12}>
        <Grid p={1.5} item xs={12}>
          <ItemHeader
            onExpandToggleClick={toggleExpandableState}
            isExpandable={isExpandable}
            isOpen={isOpen}
            showImage={isPPGBox}
            name={productDetails.name}
            imgSrc={productDetails.imgUrl}
            imageSize='large'
          />
        </Grid>
      </Grid>
      <Grid
        px={1.5}
        container
        justifyContent='space-between'
        item
        py={1}
        xs={12}
      >
        {productDetails.detail_metrics
          .slice()
          .sort((a, b) => a.display_order - b.display_order)
          .map((metric) => (
            <Grid container mr={1} item xs='auto' key={metric.kpi}>
              <Typography mr={1} color='#3D3D3D' fontSize='12px'>
                {metric.alias}
              </Typography>
              <Typography fontWeight={700} fontSize='12px'>
                {formatNumber(
                  metric.metric_format,
                  metric.metric_value,
                  metric.prefix,
                  metric.suffix
                )}
              </Typography>
            </Grid>
          ))}
        <Grid px={1.5} item xs='auto'>
          <Typography fontSize='12px' fontWeight={400} color='#8080CF'>
            {formatNumber(
              productDetails.price.metric_format,
              productDetails.price.metric_value,
              productDetails.price.prefix,
              productDetails.price.suffix
            )}
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Collapse in={isExpandable ? isOpen : true} timeout='auto'>
          <Grid container>
            <Grid
              item
              xs={12}
              sx={{ height: '1px', backgroundColor: '#C5C7EC' }}
              my={0.5}
            />
            <Grid
              px={1.5}
              item
              container
              alignItems='stretch'
              spacing={1}
              xs={12}
            >
              {productDetails.metrics
                .slice()
                .sort((a, b) => a.display_order - b.display_order)
                .map((metric) => (
                  <Grid key={metric.kpi} item sx={{ width: '20%', pl: 1 }}>
                    <Grid>
                      <Typography color='#3D3D3D' fontSize='12px'>
                        {metric.alias}
                      </Typography>
                    </Grid>
                    <Grid>
                      <Typography fontWeight={700} fontSize='12px'>
                        {formatNumber(
                          metric.metric_format,
                          metric.metric_value,
                          metric.prefix,
                          metric.suffix
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
            </Grid>
            <Grid
              item
              xs={12}
              sx={{ height: '1px', backgroundColor: '#C5C7EC' }}
              my={0.5}
            />
            <Grid px={1.5} container item xs={12}>
              <OpportunityStrip
                onOpportunitySelection={onOpportunitySelection}
                opportunities={opportunities}
                isDisabled={isOpportunitiesSelectionDisabled}
                selectedOpportunities={productDetails.selected_opportunities}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sx={{ height: '1px', backgroundColor: '#C5C7EC' }}
              my={0.5}
            />
            <Grid px={1.5} paddingTop={1} container spacing={0.5} item xs={12}>
              <Grid item xs={12}>
                <Grid container spacing={1}>
                  <CommentInputControl
                    initialComment=''
                    onCommentAdd={onCommentAdd}
                  />
                  <ProductCommentsContainer
                    isActionVisible={isCommentEditable}
                    comments={productDetails.comments}
                    onCommentDelete={onCommentDelete}
                    onCommentUpdate={onCommentUpdate}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Collapse>
      </Grid>
    </Grid>
  );
}
